import React, { createContext, useContext, useState, useEffect } from 'react';
import { registerKeycloak } from './tokenStore';

// Using dynamic import to avoid TypeScript issues
let Keycloak: any;
try {
  Keycloak = require('keycloak-js');
  // Handle both default and non-default exports
  if (typeof Keycloak !== 'function') {
    Keycloak = Keycloak.default || Keycloak;
  }
} catch (error) {
  console.error('Failed to load keycloak-js:', error);
}

interface AuthContextType {
  keycloak: any | null;
  authenticated: boolean;
  user: any | null;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  token: string | undefined;
}

const AuthContext = createContext<AuthContextType>({
  keycloak: null,
  authenticated: false,
  user: null,
  login: () => {},
  logout: () => {},
  hasRole: () => false,
  hasAnyRole: () => false,
  token: undefined,
});

export const useAuth = () => useContext(AuthContext);

interface KeycloakProviderProps {
  children: React.ReactNode;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
  const [keycloak, setKeycloak] = useState<any>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        if (!Keycloak) {
          console.error('Keycloak library not loaded');
          setIsInitialized(true);
          return;
        }

        const keycloakConfig = {
          url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080',
          realm: process.env.REACT_APP_KEYCLOAK_REALM || 'suma-credit',
          clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'suma-frontend',
        };

        console.log('Initializing Keycloak with config:', keycloakConfig);
        const keycloakInstance = new Keycloak(keycloakConfig);

        // Don't automatically require login - let the user click the login button
        const auth = await keycloakInstance.init({
          checkLoginIframe: false,
          pkceMethod: 'S256',
        });

        setKeycloak(keycloakInstance);
        registerKeycloak(keycloakInstance); // expose to axios interceptor
        setAuthenticated(auth);
        setIsInitialized(true);

        if (auth && keycloakInstance.token) {
          try {
            const profile = await keycloakInstance.loadUserProfile();
            setUser(profile);
          } catch (error) {
            console.error('Failed to load user profile:', error);
          }

          // Token refresh
          setInterval(() => {
            keycloakInstance
              .updateToken(70)
              .then((refreshed: boolean) => {
                if (refreshed) {
                  console.log('Token refreshed');
                }
              })
              .catch(() => {
                console.error('Failed to refresh token');
                setAuthenticated(false);
              });
          }, 60000);
        }
      } catch (error) {
        console.error('Keycloak initialization failed:', error);
        setIsInitialized(true);
      }
    };

    initKeycloak();
  }, []);

  const login = () => {
    if (keycloak) {
      keycloak.login();
    }
  };

  const logout = () => {
    if (keycloak) {
      keycloak.logout();
    }
  };

  const hasRole = (role: string): boolean => {
    if (!keycloak || !keycloak.tokenParsed) return false;
    const realmAccess = keycloak.tokenParsed.realm_access;
    return realmAccess?.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  const value: AuthContextType = {
    keycloak,
    authenticated,
    user,
    login,
    logout,
    hasRole,
    hasAnyRole,
    token: keycloak?.token,
  };

  if (!isInitialized) {
    return <div>Loading authentication...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};